/**
 * Split-screen staff sign-in (the LKC / Acepsle-hub pattern, Grace-Haven styled).
 *  - Left: espresso brand panel — lotus, wordmark, tagline, feature chips.
 *  - Right: email/password sign-in, gated to operator emails.
 */
import { useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { CalendarDays, Users, Receipt, Settings as Cog, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Lotus } from '@/components/ui/Lotus';
import { useAuth } from '@/lib/auth';
import { useBusiness } from '@/lib/business';

const FEATURES = [
  { icon: CalendarDays, label: 'Bookings & confirmations' },
  { icon: Users, label: 'Clients & history' },
  { icon: Receipt, label: 'Invoices & receipts' },
  { icon: Cog, label: 'Site & email settings' },
];

function describeError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password': return 'Incorrect email or password.';
    case 'auth/user-not-found': return 'No account found for that email.';
    case 'auth/too-many-requests': return 'Too many attempts. Please wait a moment.';
    case 'auth/popup-closed-by-user': return 'Google sign-in was cancelled.';
    case 'auth/popup-blocked': return 'Your browser blocked the popup. Allow popups and retry.';
    case 'auth/operation-not-allowed': return 'This sign-in method is not enabled in Firebase yet.';
    default: return 'Something went wrong. Please try again.';
  }
}

export function AdminLogin() {
  const { user, operator, signInEmail } = useAuth();
  const biz = useBusiness();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (user && operator) return <Navigate to="/admin" replace />;

  async function go(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await fn();
      navigate('/admin');
    } catch (e) {
      setError(describeError((e as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative flex flex-col justify-center overflow-hidden bg-espresso-deep px-8 py-14 text-cream lg:w-1/2 lg:px-16">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold/20 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-20 h-[26rem] w-[26rem] rounded-full bg-blush/20 blur-3xl" />

        <div className="relative z-10 mx-auto w-full max-w-md">
          <div className="flex items-center gap-3">
            <Lotus size={44} className="text-gold" />
            <div>
              <p className="wordmark text-4xl leading-none text-gold">{biz.wordmark}</p>
              <p className="font-body text-[10px] font-medium uppercase tracking-[0.45em] text-cream/70">Staff Portal</p>
            </div>
          </div>

          <h2 className="mt-10 font-display text-3xl font-medium leading-tight">
            Bookings, clients &amp; receipts — in one calm place.
          </h2>
          <p className="mt-3 font-body text-sm leading-relaxed text-cream/80">
            Confirm appointments, look after your guests, and send branded invoices for {biz.legalName}.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex items-center gap-2.5 border border-cream/15 bg-cream/5 px-3 py-2.5">
                <f.icon size={16} className="shrink-0 text-gold" />
                <span className="font-body text-xs leading-tight text-cream/90">{f.label}</span>
              </div>
            ))}
          </div>

          <p className="mt-10 font-body text-xs text-cream/50">{biz.address}</p>
        </div>
      </div>

      {/* Sign-in panel */}
      <div className="flex flex-1 items-center justify-center bg-cream px-6 py-14 lg:w-1/2">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-4 inline-block font-body text-sm font-medium text-gold-deep transition hover:text-espresso">
            ← Back to website
          </Link>
          <h1 className="font-display text-3xl text-espresso">Welcome back</h1>
          <p className="mt-1 font-body text-sm text-muted">Sign in to the Grace Haven staff portal.</p>

          {user && !operator && (
            <p className="mt-5 border border-blush-deep/40 bg-blush-soft/50 p-3 font-body text-xs text-espresso">
              Signed in as {user.email}, but that account isn't an operator yet. Ask a manager to add you under
              User Management.
            </p>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <label className="label" htmlFor="a-email">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input id="a-email" type="email" autoComplete="username" className="field !pl-9" value={email}
                  onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go(() => signInEmail(email, password))} placeholder="you@gracehavenspa.co.bw" />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="a-pass">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input id="a-pass" type={showPw ? 'text' : 'password'} autoComplete="current-password" className="field !px-9" value={password}
                  onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go(() => signInEmail(email, password))} placeholder="********" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-espresso" aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <p className="font-body text-sm text-blush-deep">{error}</p>}

            <button onClick={() => go(() => signInEmail(email, password))} disabled={busy} className="btn-primary w-full">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>

          <p className="mt-6 font-body text-xs text-muted">
            Staff accounts are created in Firebase Authentication and granted access under User Management.
          </p>
        </div>
      </div>
    </div>
  );
}
